import React from 'react';
import {
  compose,
  lifecycle,
  withStateHandlers,
  branch,
  renderComponent,
} from 'recompose';
import { serviceLocator } from '../../infrastructure/ServiceLocator';
import LayoutHelper from '../../infrastructure/LayoutHelper';
import { RouteComponentProps } from 'react-router-dom';

interface Teacher {
  _id: string;
  name?: string;
  [key: string]: any;
}

interface Studio {
  id?: string;
  owner_id: string;
  [key: string]: any;
}

interface Owner {
  _id: string;
  name?: string;
  [key: string]: any;
}

interface WithPublicStudioProps extends RouteComponentProps<{ domainName: string }> {
  setTeachers: (teachers: Teacher[]) => void;
  setStudio: (studio: Studio) => void;
  setOwner: (owner: Owner) => void;
  teachers: Teacher[];
  teachersMap: Record<string, Teacher>;
  studio?: Studio;
  owner?: Owner;
}

interface PublicStudioState {
  teachers: Teacher[];
  teachersMap: Record<string, Teacher>;
  studio?: Studio;
  owner?: Owner;
}

// Add global page state type
declare global {
  interface Window {
    __PAGE_STATE__?: {
      studio: Studio;
      studioOwner: Owner;
    };
  }
}

export const withPublicStudio = compose<React.ComponentType<WithPublicStudioProps>, any>(
  withStateHandlers<PublicStudioState, {
    setTeachers: (state: PublicStudioState) => (teachers: Teacher[]) => Partial<PublicStudioState>;
    setStudio: (state: PublicStudioState) => (studio: Studio) => Partial<PublicStudioState>;
    setOwner: (state: PublicStudioState) => (owner: Owner) => Partial<PublicStudioState>;
  }>(
    {
      teachers: [],
      teachersMap: {},
      studio: undefined,
    },
    {
      setTeachers: () => (teachers: Teacher[]) => {
        const teachersMap: Record<string, Teacher> = {};
        for (let i = 0; i < teachers.length; i++) {
          const teacher = teachers[i];
          teachersMap[teacher._id] = teacher;
        }
        return {
          teachers,
          teachersMap,
        };
      },
      setStudio: () => (studio: Studio) => {
        return {
          studio,
        };
      },
      setOwner: () => (owner: Owner) => {
        return {
          owner,
        };
      },
    }
  ),
  lifecycle<WithPublicStudioProps, {}>({
    componentWillMount() {
      const { setStudio, setTeachers, match, setOwner } = this.props;
      
      const loadTeachers = (ownerId: string) => {
        serviceLocator.teacherApi.getPublicTeachers(ownerId).then((data: { teachers: Teacher[] }) => {
          setTeachers(data.teachers);
        }, LayoutHelper.onErr);
      };

      const pageState = window.__PAGE_STATE__;
      if (pageState && pageState.studio) {
        const studio = pageState.studio;
        setStudio(studio);
        loadTeachers(studio.owner_id);
        setOwner(pageState.studioOwner);
      } else {
        const { domainName } = match.params;
        serviceLocator.studioApi
          .getStudioByDomainName(domainName)
          .then((data: Studio) => {
            setStudio(data);
            loadTeachers(data.owner_id);
          }, LayoutHelper.onErr);
      }
    },
  }),
  branch<WithPublicStudioProps>(
    ({ studio }) => !studio, 
    renderComponent(() => LayoutHelper.renderLoader())
  )
);